<template>
  <div class="d-flex manage-vehicle" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex justify-content-between mb-1">
        <div class="d-flex">
          <h4 class="card-title mt-0">
            {{ i18n.global.t('pages.vehicles.title.list') }}
          </h4>
        </div>
      </div>
      <div class="mb-5 scrollable-buttons">
        <div class="btn-area">
          <v-btn
            v-if="createAuthority"
            class="exec-btn"
            @click="moveFormView()"
          >
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
          <FileDownloadBtn
            :showable="csvtAuthorities.csvExport"
            btnClass="exec-btn mt-0 ml-1"
            :btnName="i18n.global.t('pages.common.button.csvExport')"
            :type="downloadType"
            :filterItems="makeSearchParams()"
          />
          <v-btn
            v-show="csvtAuthorities.csvImport && (isManagementOffice || isSubManagementOffice)"
            class="exec-btn mt-0 ml-1"
            @click="activateModalState"
          >
            {{ $t("pages.common.button.csvImport") }}
          </v-btn>
        </div>
        <UpsertCsvButtons
          v-if="csvtAuthorities.csvImport || csvtAuthorities.csvExport"
          :title="$t('pages.common.button.csvUpsert')"
          :importTitle="$t('pages.vehicles.csv.importTitle')"
          :importType="importType"
          :exportType="exportType"
          :filterItems="makeSearchParams()"
          :getList="getVehicles"
          :authorities="csvtAuthorities"
          elementId="vehicles-upsert"
        />
      </div>
      <div>
        <VehicleList
          class="mb-2"
          :isSearchCompleted="isSearchCompleted"
          :items="vehicleList"
          :listLoading="listLoading"
          :updatableStatusButton="changeStatusAuthority"
          @changeStatus="updateStatus"
          @changeItem="searchVehicleList"
          :showFlash="showFlash"
          v-model="sortParam"
          :isStatusUpdating="isStatusUpdating"
          :isManagementOffice="isManagementOffice"
          :isSubManagementOffice="isSubManagementOffice"
          :isOperationOffice="isOperationOffice"
        />
        <PaginateWithCount
          class="justify-end"
          v-model="currentPage"
          :totalPage="totalPage"
          :itemsPerPage="vehiclesPerPage"
          :allElementsCount="allVehiclesCount"
          :showItemCount="true"
        />
        <hr />
      </div>
    </div>
    <VehicleSearchForm
      ref="vehicleSearchForm"
      :loading="searchLoading"
      :isSubManagementOffice="isSubManagementOffice"
      :isManagementOffice="isManagementOffice"
      :isDepartment="isDepartment"
      @formchange="handleChangeSearchForm"
      @search="searchVehicleList"
      @clear="clearVehicleSearchParams"
    />
    <FileUploadModal
      :modalState="modalState"
      :title="$t('pages.vehicles.csv.importTitle')"
      uploadBtnClass='exec-btn mt-0 ml-1'
      closeBtnClass='mt-0'
      :type="uploadType"
      templateCsvFileType="vehicles"
      @closeModal="inactivateModalState"
    />
  </div>
</template>

<script>
  import { MANAGEMENT_OFFICE_TYPE, SUB_MANAGEMENT_OFFICE_TYPE, OPERATION_OFFICE_TYPE, DEPARTMENT_OFFICE_TYPE } from "@/services/consts.ts";
  import VehicleSearchForm from "@/components/VehicleParts/VehicleSearchForm";
  import VehicleList from "@/components/VehicleParts/VehicleList";
  import FileDownloadBtn from "@/components/Files/DownloadButton";
  import FileUploadModal from "@/components/Files/UploadModal";
  import Api from "@/services/api/ApiServiceFabrick";
  import AuthorityCacheApi from "@/services/models/AuthorityCacheApi";
  import { useAuth } from "@/services/user/Auth0UserProvider";
  import UpsertCsvButtons from "@/components/Common/UpsertCsvButtons";
  import i18n from '@/lang/i18n';
  import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";
  import PaginateWithCount from "@/components/PaginateWithCount.vue";


  export default {
    props: {
      showFlash: {
        type: Function
      }
    },
    data() {
      return {
        form: {},
        currentPage: 1,
        totalPage: 0,
        allVehiclesCount: 0,
        vehiclesPerPage: 50,
        vehicleList: [],
        selectedDeliveryPlanIds: [],
        selectOutputOptions: {},
        searchLoading: false,
        listLoading: false,
        isSearchCompleted: false,
        beforeSearchParams: null,
        createAuthority: false,
        changeStatusAuthority: false,
        loginWithRedirect: null,
        sortParam: {},
        downloadType: "App\\Services\\Csv\\Exports\\VehicleService",
        uploadType: "App\\Services\\Csv\\Imports\\VehicleService",
        importType: "App\\Services\\Csv\\Upserts\\Imports\\VehicleService",
        exportType: "App\\Services\\Csv\\Upserts\\Exports\\VehicleService",
        modalState: false,
        isSubManagementOffice: false,
        isManagementOffice: false,
        isOperationOffice: false,
        csvtAuthorities: {
          csvImport: false,
          csvExport: false
        },
        isStatusUpdating: false
      }
    },
    computed: {
      borderWidth() {
        return '1000';
      }
    },
    setup() {
      document.title = i18n.global.t('pages.vehicles.title.list')
      return {
        i18n
      }
    },
    mounted() {
      this.getAuthorities()
      const { loginWithRedirect } = useAuth()
      this.loginWithRedirect = loginWithRedirect
      Api().getSelfAccount().then((data) => {
        this.isSubManagementOffice = data.login_organization_type == SUB_MANAGEMENT_OFFICE_TYPE
        this.isManagementOffice = data.login_organization_type == MANAGEMENT_OFFICE_TYPE
        this.isDepartment = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
        this.isOperationOffice = data.login_organization_type == OPERATION_OFFICE_TYPE || data.login_organization_type == DEPARTMENT_OFFICE_TYPE
        const savedSearchParams = loadDataFromSessionStorage('vehicleSearchParams')
        if (savedSearchParams) {
          this.getVehicles(savedSearchParams)
        } else if (!this.isManagementOffice) {
          this.getVehicles(this.createRequest())
        }
      })
    },
    watch: {
      currentPage: {
        handler() {
          this.getVehicles(this.createRequest(this.currentPage, false))
        }
      },
      sortParam: {
        handler() {
          this.getVehicles(this.createRequest(1))
        }
      },
    },
    methods: {
      async getAuthorities(isError = false) {
        if (isError) {
          await AuthorityCacheApi.reflesh()
        }
        const authorityList = await AuthorityCacheApi.getWithParse()
        this.createAuthority = !!authorityList["octlink.vehicle.create"]
        this.changeStatusAuthority = !!authorityList["octlink.vehicle.delete"];
        this.csvtAuthorities.csvImport = !!authorityList["octlink.vehicle.input"]
        this.csvtAuthorities.csvExport = !!authorityList["octlink.vehicle.output"]
      },
      handleChangeSearchForm(form) {
        this.form = form
      },
      searchVehicleList() {
        this.searchLoading = true
        const req = this.createRequest()
        this.getVehicles(req)
        saveDataToSessionStorage('vehicleSearchParams', req)
        this.$refs.vehicleSearchForm.hideFilterIfMobile()
      },
      moveFormView() {
        this.$router.push('/vehicles/form')
      },
      createRequest(page = 1, updateOption = true) {
        let req
        if (updateOption || !this.beforeSearchParams) {
          req = this.makeSearchParams(page)
          this.beforeSearchParams = req
          return req
        } else {
          req = this.beforeSearchParams
          req.page = page
          return req
        }
      },
      getVehicles(req) {
        this.listLoading = true

        Api().searchVehicles(req).then((data) => {
          this.vehicleList = data.items
          this.currentPage = data.current_page
          this.totalPage = data.total_page
          this.allVehiclesCount = data.total
          this.searchLoading = false
          this.listLoading = false
          this.isSearchCompleted = true
        }).catch((error) => {
          const res = error.response
          switch (res.status) {
            case 401:
              this.loginWithRedirect({ appState: { targetUrl: location.pathname } })
              break
            case 403:
              location.reload()
              break
            case 422:
              this.showFlash(i18n.global.t('pages.vehicles.errors.getList'), "error", false)
          }
          this.listLoading = false
          this.searchLoading = false
        })
      },
      makeSearchParams(page = undefined){
        const ret = {
          page: page,
          operation_office_id: this.form?.operationOffice?.id,
          management_officeable_id: this.form?.managementOffice?.id,
          management_officeable_type: this.form?.managementOffice?.type,
          department_id: this.form?.department?.id,
          frames_number: this.form?.frames_number,
          registration_number: this.form?.registration_number,
          number_plate: this.form?.number_plate,
          digitaco_model_number: this.form?.model_number?.id,
          device_id: this.form?.device?.id,
          fw_version: this.form?.fw_version,
          sort: this.sortParam?.sort,
          sort_type: this.sortParam?.type,
          status: this.form?.status,
          enabled_camera_front: this.form?.peripheralEquipmentCheckBox?.enabledCameraFront,
          enabled_camera_rear: this.form?.peripheralEquipmentCheckBox?.enabledCameraRear,
          enabled_camera_inner: this.form?.peripheralEquipmentCheckBox?.enabledCameraInner,
          enabled_camera_side: this.form?.peripheralEquipmentCheckBox?.enabledCameraSide,
          enabled_tdsensor_ch1: this.form?.peripheralEquipmentCheckBox?.enabledTdsensorCh1,
          enabled_tdsensor_ch2: this.form?.peripheralEquipmentCheckBox?.enabledTdsensorCh2,
          enabled_tdsensor_ch3: this.form?.peripheralEquipmentCheckBox?.enabledTdsensorCh3,
          enabled_tdsensor_ch4: this.form?.peripheralEquipmentCheckBox?.enabledTdsensorCh4,
          enabled_etc: this.form?.peripheralEquipmentCheckBox?.enabledEtc,
        }
        return ret
      },
      convertDateTimeTodate(datetime) {
        const ret = datetime?.toISOString() || ''
        return ret.split('T')[0].replaceAll('-', '/')
      },
      inactivateModalState(isReacquireVehicle) {
        this.modalState = false;
        if (isReacquireVehicle) {
          this.getVehicles(this.createRequest());
        }
      },
      activateModalState() {
        this.modalState = true;
      },
      updateStatus(params, status) {
        this.isStatusUpdating = true;
        Api().updateVehicleStatus(params, status).then((_data) => {
          this.showFlash(this.$t("pages.common.messages.changeStatusSuccess"), "success");
          this.isStatusUpdating = false;
          this.getVehicles(this.createRequest());
        }).catch((error) => {
          const res = error.response;
          switch (res.status) {
            case 401:
              this.loginWithRedirect({
                appState: { targetUrl: location.pathname },
              });
              break;
            case 403:
              this.showFlash(this.$t("pages.common.messages.forbidden"), "error", false)
              this.getAuthorities(true);
              break;
            case 422:
              this.showFlash(
                this.$t("pages.common.messages.changeStatusFail"),
                "error",
                false
              );
          }
          this.isStatusUpdating = false;
        });
      },
      clearVehicleSearchParams() {
        clearSessionStorage('vehicleSearchParams')
      },
    },
    components: {
      VehicleSearchForm,
      VehicleList,
      FileDownloadBtn,
      FileUploadModal,
      UpsertCsvButtons,
      PaginateWithCount,
    }
  }
</script>

<style>
.main-panel {
  height: initial !important;
  max-height: initial !important;
}
.exec-btn {
  background-color: rgb(21, 130, 156);
  color: white !important;
}
.reset-btn {
  background-color: rgb(245, 246, 247);
  color: rgb(21, 130, 156) !important;
  font-weight: bold;
}

.scrollable-buttons {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 840px) {
  .content {
    position: relative;
    z-index: 0;
  }
  .manage-vehicle {
    min-height: 100vh;
    overflow-y: auto;
  }
  .scrollable-buttons {
    display: flex;
    justify-content: start;
    overflow-x: auto;
    white-space: nowrap;
  }
  .scrollable-buttons::-webkit-scrollbar {
    display: none;
  }
}

</style>
